import { computed, ref, watch } from "vue";
import { Config } from "tf-api-client";
import { THEME_KEY } from "../config/constants";
import { useSettings } from "./use-settings";

const { Theme } = Config;

// The theme the user picked (System / Light / Dark). Seeded from whatever
// index.html's inline bootstrap script already read from localStorage, so
// there's no jump once this module takes over.
const storedTheme = localStorage.getItem(THEME_KEY) as Config.Theme | null;
const theme = ref<Config.Theme>(
  storedTheme && Object.values(Theme).includes(storedTheme)
    ? storedTheme
    : Theme.System,
);

const prefersDarkMedia =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : undefined;
const systemPrefersDark = ref(prefersDarkMedia?.matches ?? false);

prefersDarkMedia?.addEventListener("change", (event) => {
  systemPrefersDark.value = event.matches;
});

// What actually gets applied to the DOM once "system" is resolved.
const resolvedTheme = computed<
  typeof Config.Theme.Light | typeof Config.Theme.Dark
>(() =>
  theme.value === Theme.System
    ? systemPrefersDark.value
      ? Theme.Dark
      : Theme.Light
    : theme.value,
);

const applyTheme = () => {
  document.documentElement.classList.toggle(
    "dark",
    resolvedTheme.value === Theme.Dark,
  );
};

watch(resolvedTheme, applyTheme, { immediate: true });

let synced = false;

export function useTheme() {
  const { settings, getSettingValue, updateSettings } = useSettings();

  // Once the backend settings arrive, they're the source of truth (a user
  // switching devices should see their saved preference, not this device's
  // last local choice).
  watch(
    settings,
    (newSettings) => {
      if (!newSettings || synced) return;
      synced = true;
      theme.value = getSettingValue(Config.SettingKind.Theme, {
        default: theme.value,
      });
    },
    { immediate: true },
  );

  const setTheme = (value: Config.Theme) => {
    theme.value = value;
    localStorage.setItem(THEME_KEY, value);
    updateSettings([
      {
        kind: Config.SettingKind.Theme,
        value,
        type: "string",
      },
    ]);
  };

  return {
    theme,
    resolvedTheme,
    setTheme,
    Theme,
  };
}
